import React, { useState, useEffect } from 'react';
import withRoleAccess from '../../hoc/withRoleAccess';
import {
  Card,
  Row,
  Col,
  DatePicker,
  Button,
  Statistic,
  Spin,
  Alert,
  Typography,
  Space,
  Tag,
  Progress,
  Divider,
  message
} from 'antd';
import {
  UserOutlined,
  UserAddOutlined,
  ReloadOutlined,
  DollarOutlined,
  TrophyOutlined,
  CalendarOutlined,
  FilePdfOutlined
} from '@ant-design/icons';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import axios from 'axios';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const API_URL = process.env.REACT_APP_API_URL;

const StudentReport = () => {
  const [dateRange, setDateRange] = useState([
    dayjs().subtract(30, 'day'),
    dayjs()
  ]);
  
  const [summaryData, setSummaryData] = useState(null);
  const [detailsData, setDetailsData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Real API calls using axios
  const fetchSummaryData = async () => {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${API_URL}/report/student/summary`, {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        from: dateRange[0].format('YYYY-MM-DD'),
        to: dateRange[1].format('YYYY-MM-DD')
      }
    });
    return response.data;
  };

  const fetchDetailsData = async () => {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${API_URL}/report/student/new/details`, {
      headers: { Authorization: `Bearer ${token}` },
      params: {
        from: dateRange[0].format('YYYY-MM-DD'),
        to: dateRange[1].format('YYYY-MM-DD')
      }
    });
    return response.data;
  };

  const loadData = async () => {
    if (!API_URL) {
      setError('API URL is not configured. Please set REACT_APP_API_URL in your environment variables.');
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setError('Authentication token not found. Please log in again.');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const [summary, details] = await Promise.all([
        fetchSummaryData(),
        fetchDetailsData()
      ]);
      
      setSummaryData(summary);
      setDetailsData(details);
      
      message.success('Report data loaded successfully');
    } catch (err) {
      console.error('Error loading report data:', err);
      
      let errorMessage = 'Failed to load report data';
      if (err.response) {
        // Server responded with error status
        if (err.response.status === 401) {
          errorMessage = 'Authentication failed. Please log in again.';
        } else if (err.response.status === 400) {
          errorMessage = err.response.data?.message || 'Invalid request parameters';
        } else {
          errorMessage = err.response.data?.message || `Server error: ${err.response.status}`;
        }
      } else if (err.request) {
        // Request made but no response received
        errorMessage = 'Network error. Please check your connection.';
      } else {
        // Something else happened
        errorMessage = err.message || 'An unexpected error occurred';
      }
      
      setError(errorMessage);
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (dateRange && dateRange.length === 2 && dateRange[0] && dateRange[1]) {
      loadData();
    }
  }, [dateRange]);

  const handleDateRangeChange = (dates) => {
    if (dates && dates.length === 2) {
      setDateRange(dates);
    }
  };

  const handleManualRefresh = () => {
    loadData();
  };
const handlePrintPDF = () => {
 // Open a new window for printing
 const printWindow = window.open('', '_blank', 'width=800,height=600');
 
 // Get the content to print
 const reportContainer = document.getElementById('student-report-container');
 const printContent = reportContainer ? reportContainer.innerHTML : '';
 
 // Create the print document
 const printDocument = `
   <!DOCTYPE html>
   <html>
     <head>
       <title>Student Analytics Report</title>
       <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/antd/5.0.0/reset.css">
       <style>
         body { 
           font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
           margin: 0;
           padding: 20px;
           background: white;
         }
         .ant-card { 
           box-shadow: none !important; 
           border: 1px solid #d9d9d9 !important;
           margin-bottom: 16px;
           page-break-inside: avoid;
         }
         .ant-statistic-title { font-weight: bold !important; }
         .ant-statistic-content { font-size: 18px !important; }
         .ant-card-head { 
           border-bottom: 2px solid #1890ff !important; 
           background: #f0f2f5 !important;
         }
         .ant-card-head-title { 
           font-weight: bold !important; 
           color: #1890ff !important; 
         }
         .ant-progress-outer { 
           display: inline-block !important; 
           width: 200px !important; 
         }
         .print-header { 
           display: block !important;
           text-align: center; 
           margin-bottom: 20px; 
           border-bottom: 3px solid #1890ff; 
           padding-bottom: 10px; 
         }
         .print-date-range { 
           display: block !important;
           text-align: center; 
           margin-bottom: 15px; 
           font-weight: bold; 
         }
         svg { max-width: 100% !important; height: auto !important; }
         .no-print { display: none !important; }
         @page { 
           margin: 15mm; 
           size: A4; 
         }
       </style>
     </head>
     <body>
       ${printContent}
       <script>
         window.onload = function() {
           window.print();
           window.onafterprint = function() {
             window.close();
           };
         };
       </script>
     </body>
   </html>
 `;
 
 // Write the document and trigger print
 printWindow.document.write(printDocument);
 printWindow.document.close();
};

  // Prepare chart data
  const ageDistributionData = detailsData ? [
    { name: 'Under 10', value: parseInt(detailsData.age_under_10) || 0, color: '#1890ff' },
    { name: '10-12', value: parseInt(detailsData.age_10_12) || 0, color: '#52c41a' },
    { name: '13-15', value: parseInt(detailsData.age_13_15) || 0, color: '#faad14' },
    { name: '16-18', value: parseInt(detailsData.age_16_18) || 0, color: '#f5222d' },
    { name: 'Above 18', value: parseInt(detailsData.age_above_18) || 0, color: '#722ed1' }
  ].filter(item => item.value > 0) : []; // Only show age groups with data

  const genderData = detailsData ? [
    { name: 'Male', value: parseInt(detailsData.male_count) || 0, color: '#1890ff' },
    { name: 'Female', value: parseInt(detailsData.female_count) || 0, color: '#eb2f96' },
    { name: 'Unspecified', value: parseInt(detailsData.unspecified_gender_count) || 0, color: '#8c8c8c' }
  ].filter(item => item.value > 0) : []; // Only show genders with data

  const studentTypeData = summaryData ? [
    { name: 'New Students', value: parseInt(summaryData.new_students) || 0, color: '#52c41a' },
    { name: 'Repeat Students', value: parseInt(summaryData.repeat_students) || 0, color: '#1890ff' }
  ].filter(item => item.value > 0) : [];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold">{`${label}`}</p>
          <p className="text-blue-600">
            {`Count: ${payload[0].value}`}
          </p>
        </div>
      );
    }
    return null;
  };

  const PieTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-semibold">{`${payload[0].name}`}</p>
          <p className="text-blue-600">
            {`Count: ${payload[0].value}`}
          </p>
        </div>
      );
    }
    return null;
  };

  return (
    <div style={{ padding: '24px', backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <div id="student-report-container" style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Print Header - Only visible when printing */}
        <div className="print-header" style={{ display: 'none' }}>
          <Title level={2} style={{ margin: 0, color: '#1890ff' }}>
            Student Analytics Report
          </Title>
          <Text>Generated on {new Date().toLocaleDateString()}</Text>
        </div>

        {/* Print Date Range - Only visible when printing */}
        <div className="print-date-range" style={{ display: 'none' }}>
          Report Period: {dateRange[0]?.format('YYYY-MM-DD')} to {dateRange[1]?.format('YYYY-MM-DD')}
        </div>
        {/* Header */}
        <div style={{ marginBottom: '24px' }}>
          <Title level={2} style={{ marginBottom: '8px' }}>
            Student Analytics Report
          </Title>
          <Text type="secondary">
            Comprehensive overview of student enrollment and demographics
          </Text>
        </div>

        {/* Date Range and Controls */}
        <Card style={{ marginBottom: '24px' }}>
          <Space size="large" wrap>
            <Space>
              <CalendarOutlined />
              <Text strong>Date Range:</Text>
              <RangePicker
                value={dateRange}
                onChange={handleDateRangeChange}
                format="YYYY-MM-DD"
                allowClear={false}
              />
            </Space>
            <Space>
              <Button 
                type="primary" 
                icon={<ReloadOutlined />}
                onClick={handleManualRefresh}
                loading={loading}
              >
                Update Report
              </Button>
              <Button 
                type="default" 
                icon={<FilePdfOutlined />}
                onClick={handlePrintPDF}
                disabled={!summaryData && !detailsData}
              >
                Print to PDF
              </Button>
            </Space>
          </Space>
        </Card>

        {error && (
          <Alert
            message="Error"
            description={error}
            type="error"
            showIcon
            style={{ marginBottom: '24px' }}
          />
        )}

        <Spin spinning={loading}>
          {/* Key Metrics */}
          {summaryData && (
            <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic
                    title="Total Students"
                    value={summaryData.total_students}
                    prefix={<UserOutlined />}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic
                    title="New Students"
                    value={summaryData.new_students}
                    prefix={<UserAddOutlined />}
                    valueStyle={{ color: '#52c41a' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic
                    title="Repeat Students"
                    value={summaryData.repeat_students}
                    prefix={<ReloadOutlined />}
                    valueStyle={{ color: '#1890ff' }}
                  />
                </Card>
              </Col>
              <Col xs={24} sm={12} lg={6}>
                <Card>
                  <Statistic
                    title="Avg. Spending"
                    value={summaryData.avg_spending_per_student}
                    prefix={<DollarOutlined />}
                    precision={2}
                    valueStyle={{ color: '#722ed1' }}
                  />
                </Card>
              </Col>
            </Row>
          )}

          {/* Charts Row */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            {/* Student Type Distribution */}
            <Col xs={24} lg={12}>
              <Card 
                title="Student Type Distribution"
                extra={<TrophyOutlined />}
              >
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={studentTypeData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="value" fill="#1890ff" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </Col>

            {/* Age Distribution */}
            <Col xs={24} lg={12}>
              <Card title="Age Distribution">
                {ageDistributionData.length > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={ageDistributionData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent, value }) => `${name} (${value})`}
                          outerRadius={100}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {ageDistributionData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip content={<PieTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div style={{ textAlign: 'center', marginTop: '16px' }}>
                      <Space size="small" wrap>
                        {ageDistributionData.map((entry) => (
                          <Space key={entry.name} size="small">
                            <div 
                              style={{ 
                                width: '12px', 
                                height: '12px', 
                                backgroundColor: entry.color,
                                borderRadius: '50%' 
                              }}
                            />
                            <Text style={{ fontSize: '12px' }}>{entry.name}: {entry.value}</Text>
                          </Space>
                        ))}
                      </Space>
                    </div>
                  </>
                ) : (
                  <div style={{ textAlign: 'center', padding: '60px 0' }}>
                    <Text type="secondary">No age distribution data available</Text>
                  </div>
                )}
              </Card>
            </Col>
          </Row>

          {/* Second Row of Charts */}
          <Row gutter={[16, 16]} style={{ marginBottom: '24px' }}>
            {/* Gender Distribution */}
            <Col xs={24} lg={12}>
              <Card title="Gender Distribution">
                {genderData.length > 0 ? (
                  <>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={genderData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={5}
                          dataKey="value"
                        >
                          {genderData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <Tooltip content={<PieTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                    <div style={{ textAlign: 'center', marginTop: '16px' }}>
                      <Space size="large">
                        {genderData.map((entry) => (
                          <Space key={entry.name}>
                            <div 
                              style={{ 
                                width: '12px', 
                                height: '12px', 
                                backgroundColor: entry.color,
                                borderRadius: '50%' 
                              }}
                            />
                            <Text>{entry.name}: {entry.value}</Text>
                          </Space>
                        ))}
                      </Space>
                    </div>
                  </>
                ) : (
                  <div style={{ textAlign: 'center', padding: '60px 0' }}>
                    <Text type="secondary">No gender distribution data available</Text>
                  </div>
                )}
              </Card>
            </Col>

            {/* Key Insights */}
            <Col xs={24} lg={12}>
              <Card title="Key Insights & Metrics">
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  {summaryData && (
                    <>
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <Text>New Student Rate</Text>
                          <Text strong style={{ color: '#52c41a' }}>
                            {((summaryData.new_students / summaryData.total_students) * 100).toFixed(1)}%
                          </Text>
                        </div>
                        <Progress 
                          percent={(summaryData.new_students / summaryData.total_students) * 100} 
                          strokeColor="#52c41a"
                          showInfo={false}
                        />
                      </div>
                      
                      <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <Text>Retention Rate</Text>
                          <Text strong style={{ color: '#1890ff' }}>
                            {((summaryData.repeat_students / summaryData.total_students) * 100).toFixed(1)}%
                          </Text>
                        </div>
                        <Progress 
                          percent={(summaryData.repeat_students / summaryData.total_students) * 100} 
                          strokeColor="#1890ff"
                          showInfo={false}
                        />
                      </div>

                      <Divider />
                      
                      <Row gutter={16}>
                        <Col span={12}>
                          <Statistic
                            title="Revenue Potential"
                            value={(summaryData.avg_spending_per_student * summaryData.total_students)}
                            prefix="$"
                            precision={0}
                            valueStyle={{ fontSize: '18px' }}
                          />
                        </Col>
                        <Col span={12}>
                          {detailsData && (
                            <div>
                              <Text type="secondary">Dominant Age Group</Text>
                              <br />
                              <Text strong style={{ fontSize: '18px' }}>
                                {ageDistributionData.length > 0 
                                  ? ageDistributionData.reduce((max, current) => 
                                      current.value > max.value ? current : max
                                    ).name
                                  : 'N/A'
                                }
                              </Text>
                            </div>
                          )}
                        </Col>
                      </Row>
                    </>
                  )}
                </Space>
              </Card>
            </Col>
          </Row>

          {/* Education Levels */}
          {detailsData && detailsData.education_levels && Array.isArray(detailsData.education_levels) && (
            <Card title="Education Levels Represented">
              <Space size={[0, 8]} wrap>
                {detailsData.education_levels
                  .filter(level => level && level.trim() !== '') // Filter out null, undefined, or empty strings
                  .map((level, index) => (
                    <Tag key={index} color="blue" style={{ margin: '4px' }}>
                      {level}
                    </Tag>
                  ))}
              </Space>
              {(!detailsData.education_levels.some(level => level && level.trim() !== '')) && (
                <Text type="secondary">No education level data available</Text>
              )}
            </Card>
          )}
        </Spin>
      </div>
    </div>
  );
};

export default withRoleAccess(StudentReport, ['superadmin']);